/**
 * Mission Catalog API Client
 *
 * Provides a clean interface to the mission catalog backend API.
 * Handles mission scenarios, theaters, platforms, and air defense systems.
 */

import { apiErrorFromResponse } from './apiError';

class APIClient {
  constructor(baseURL = null) {
    // Use VITE_API_BASE_URL and append /api, matching ApiService.js pattern
    const baseUrl = baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    this.baseURL = `${baseUrl}/api`;
    this.authToken = null;
    // Optional on-demand token provider (e.g. Auth0 getAccessTokenSilently).
    // When set, request() resolves a fresh, valid token right before each call so
    // auth-protected endpoints (COA save, agent endpoints) never fail because a
    // previously-cached token was cleared or expired between page-load and action.
    this.tokenProvider = null;
  }

  /**
   * Set Auth0 access token for API requests
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Clear Auth0 access token
   */
  clearAuthToken() {
    this.authToken = null;
  }

  /**
   * Register an async token provider (Auth0 getAccessTokenSilently). Called
   * before each request to obtain a valid access token on demand.
   */
  setTokenProvider(fn) {
    this.tokenProvider = fn;
  }

  clearTokenProvider() {
    this.tokenProvider = null;
  }

  /**
   * Resolve a valid bearer token for a request: prefer an on-demand fresh token
   * from the provider (cached-if-valid by the Auth0 SDK), falling back to the
   * last synced token. Never throws — a null result just means no Authorization
   * header is sent (public endpoints still work; protected ones 403 as before).
   */
  async resolveToken() {
    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          this.authToken = token; // keep the synced copy fresh
          return token;
        }
      } catch (err) {
        console.warn('⚠️ Could not obtain a fresh access token:', err?.message || err);
      }
    }
    return this.authToken;
  }

  /**
   * Generic request wrapper with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Resolve a valid token on demand (fresh from Auth0 when possible) so that a
    // previously-cleared/expired cached token doesn't cause protected calls to 403.
    const token = await this.resolveToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw await apiErrorFromResponse(response);
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error.message);
      throw error;
    }
  }

  // ==================== Theaters ====================

  /**
   * List all available theaters
   * @returns {Promise<Array>} List of theater objects
   */
  async listTheaters() {
    return this.request('/catalog/theaters');
  }

  /**
   * List objective-to-target-type mappings
   * @param {boolean} activeOnly - Filter to active mappings only
   * @returns {Promise<Array>} List of objective mapping objects
   */
  async listObjectiveMappings(activeOnly = true) {
    const endpoint = activeOnly
      ? '/catalog/objective-mappings?active_only=true'
      : '/catalog/objective-mappings';
    return this.request(endpoint);
  }

  // ==================== Missions ====================

  /**
   * List all missions with optional filters
   * @param {Object} filters - Optional filters { theater_id, category, include_archived }
   * @returns {Promise<Array>} List of mission objects with nested targets
   */
  async listMissions(filters = {}) {
    const params = new URLSearchParams();
    if (filters.theater_id) params.append('theater_id', filters.theater_id);
    if (filters.category) params.append('category', filters.category);
    if (filters.include_archived) params.append('include_archived', 'true');

    const queryString = params.toString();
    const endpoint = queryString ? `/catalog/missions?${queryString}` : '/catalog/missions';

    return this.request(endpoint);
  }

  /**
   * Get specific mission by mission_id
   * @param {string} missionId - Mission identifier (e.g., 'counter_wmd_iran')
   * @returns {Promise<Object>} Mission object with nested targets
   */
  async getMission(missionId) {
    return this.request(`/catalog/missions/${missionId}`);
  }

  /**
   * Create new mission with targets
   * @param {Object} missionData - Mission data conforming to MissionCreate schema
   * @returns {Promise<Object>} Created mission object
   */
  async createMission(missionData) {
    return this.request('/catalog/missions', {
      method: 'POST',
      body: JSON.stringify(missionData),
    });
  }

  /**
   * Save a target-by-target mission plan as a variant of its parent mission, so it
   * appears in the Mission Catalog grouped with the parent's variants (a pre-planned
   * mission, ready to be taken into a strike plan).
   * @param {string} missionId - Parent mission id (business mission_id or UUID)
   * @param {Object} body - { name, objective, targets: [{target_id, priority}], plan_payload }
   * @returns {Promise<{variant_id: string, created: boolean, targets_assigned: number}>}
   */
  async saveMissionPlanToCatalog(missionId, { name, objective, targets, plan_payload }) {
    return this.request(`/catalog/missions/${missionId}/plan`, {
      method: 'POST',
      body: JSON.stringify({ name, objective, targets, plan_payload }),
    });
  }

  /**
   * First Planned Waypoints for a theater — where Mission Planning routes
   * start. Location only (an FPW is not a firing unit). An unknown theater
   * has none on record: [] rather than another theater's list.
   * @returns {Promise<{theater_id: string, fpws: Array}>}
   */
  async getFpws(theaterId) {
    return this.request(`/catalog/fpws?theater_id=${encodeURIComponent(theaterId)}`);
  }

  /**
   * Strike timing for each of a mission's (or mission plan's) targets, as
   * Weaponeering set it: the JDPIs' mode and ripple interval, an explicit
   * 'jdpi_conflict', or the labeled doctrinal default.
   * @returns {Promise<{mission_id, defaults: {mode, rippleIntervalSec, label}, targets: Array}>}
   */
  async getMissionStrikeTiming(missionId) {
    return this.request(`/catalog/missions/${encodeURIComponent(missionId)}/strike-timing`);
  }

  /**
   * Each of a mission's targets with its DECIDED JDPIs (EP-42 Phase 5), each
   * the exact snapshot a schemaVersion 3 plan carries — echo it back unchanged.
   * @returns {Promise<{mission_id, targets: Array<{targetId, targetOnRecord, facilityId, jdpis: Array}>}>}
   */
  async getMissionJdpis(missionId) {
    return this.request(`/catalog/missions/${encodeURIComponent(missionId)}/jdpis`);
  }

  /**
   * Create the mission's generated default Mission Planning plan when it has
   * none (idempotent). `regenerate` rebuilds it only while still generated.
   * @returns {Promise<{variant_id, mission_id, created, regenerated}>}
   */
  async generateDefaultMissionPlan(missionId, { regenerate = false } = {}) {
    return this.request(`/catalog/missions/${encodeURIComponent(missionId)}/plan/generate-default`, {
      method: 'POST',
      body: JSON.stringify({ regenerate }),
    });
  }

  /**
   * Every strike plan — one entry per catalog row holding execution data,
   * on parent missions (older plans) and Mission Planning plan variants.
   * @returns {Promise<{strike_plans: Array}>}
   */
  async listStrikePlans() {
    return this.request('/catalog/strike-plans');
  }

  /**
   * Delete mission by mission_id (cascades to targets)
   * @param {string} missionId - Mission identifier
   * @returns {Promise<null>}
   */
  async deleteMission(missionId) {
    return this.request(`/catalog/missions/${missionId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Platforms ====================

  /**
   * List all launch platforms with optional theater filter
   * @param {string} theaterId - Optional theater filter
   * @returns {Promise<Array>} List of platform objects
   */
  async listPlatforms(theaterId = null) {
    const endpoint = theaterId
      ? `/catalog/platforms?theater_id=${theaterId}`
      : '/catalog/platforms';

    return this.request(endpoint);
  }

  // ==================== Air Defenses ====================

  /**
   * List all air defense systems with optional theater filter
   * @param {string} theaterId - Optional theater filter
   * @returns {Promise<Array>} List of air defense objects
   */
  async listAirDefenses(theaterId = null) {
    const endpoint = theaterId
      ? `/catalog/air-defenses?theater_id=${theaterId}`
      : '/catalog/air-defenses';

    return this.request(endpoint);
  }

  // ==================== Intelligence Gathering ====================

  /**
   * Start AI-powered intelligence search for a focus area
   * @param {Object} params - Search parameters
   * @param {string} params.focus_area - Primary geographic area (e.g., "Yemen", "Syria")
   * @param {string} params.theater_id - Optional theater identifier
   * @param {Array<string>} params.target_types - Optional target types to prioritize
   * @param {boolean} params.include_frontier - Include frontier buffer zones (default: true)
   * @returns {Promise<Object>} Search results with session ID
   */
  async startIntelligenceSearch(params) {
    return this.request('/intelligence/search', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get targets from an intelligence search session
   * @param {string} searchSessionId - Search session UUID
   * @param {Object} filters - Optional filters
   * @param {string} filters.review_status - Filter by review status (pending|approved|rejected)
   * @param {string} filters.location_classification - Filter by location (primary|frontier)
   * @param {number} filters.min_confidence - Minimum confidence score (1-5)
   * @returns {Promise<Array>} List of discovered targets
   */
  async getIntelligenceTargets(searchSessionId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.review_status) params.append('review_status', filters.review_status);
    if (filters.location_classification) params.append('location_classification', filters.location_classification);
    if (filters.min_confidence) params.append('min_confidence', filters.min_confidence);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/intelligence/sessions/${searchSessionId}/targets?${queryString}`
      : `/intelligence/sessions/${searchSessionId}/targets`;

    return this.request(endpoint);
  }

  /**
   * Bulk approve or reject intelligence targets
   * @param {Object} params - Bulk review parameters
   * @param {Array<string>} params.target_ids - List of target UUIDs
   * @param {string} params.action - 'approve' or 'reject'
   * @param {string} params.reviewed_by - Username of reviewer
   * @param {string} missionId - Optional mission ID to add approved targets to
   * @returns {Promise<Object>} Review results
   */
  async bulkReviewTargets(params, missionId = null) {
    console.log('🌐 APIClient.bulkReviewTargets called');
    console.log('🌐 Params:', params);
    console.log('🌐 Mission ID:', missionId);

    const endpoint = missionId
      ? `/intelligence/review/bulk?mission_id=${missionId}`
      : '/intelligence/review/bulk';

    console.log('🌐 Target endpoint:', endpoint);
    console.log('🌐 Full URL:', `${this.baseURL}${endpoint}`);

    try {
      const result = await this.request(endpoint, {
        method: 'POST',
        body: JSON.stringify(params),
      });
      console.log('🌐 API request completed successfully:', result);
      return result;
    } catch (error) {
      console.error('🌐 API request failed:', error);
      throw error;
    }
  }

  /**
   * Update individual intelligence target
   * @param {string} targetId - Target UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated target
   */
  async updateIntelligenceTarget(targetId, updates) {
    return this.request(`/intelligence/targets/${targetId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ==================== Course of Action (COA) Management ====================

  /**
   * Save 3 COA variants to a mission plan
   * @param {string} missionPlanId - Mission plan UUID
   * @param {Array} coas - Array of 3 COA variants
   * @param {string} recommendedCoaId - ID of the recommended COA
   * @param {string} catalogMissionId - Optional catalog mission ID for tracking
   * @returns {Promise<Object>} Save result with workflow stage update
   */
  async saveCOAs(missionPlanId, coas, recommendedCoaId, catalogMissionId = null) {
    console.log(`💾 Saving ${coas.length} COAs to mission plan: ${missionPlanId}`);
    console.log(`   → Recommended COA: ${recommendedCoaId}`);
    if (catalogMissionId) {
      console.log(`   → Catalog Mission ID: ${catalogMissionId}`);
    }

    // Transform COAs to match backend schema
    const transformedCOAs = coas.map(coa => ({
      id: coa.id,
      name: coa.name,
      description: coa.description,
      optimizationPriority: coa.optimizationPriority || coa.name, // Use name as fallback
      totalMissiles: coa.summary?.totalMissiles || 0,
      totalCost_usd: coa.summary?.totalCost_usd || 0,
      expectedSuccessRate: coa.summary?.avgProbabilityOfSuccess || 0,
      // Not modeled: null, never an invented figure.
      expectedSurvivability: coa.summary?.avgPlatformSurvival ?? null,
      timeToEffect_minutes: coa.summary?.maxTimeToTarget_minutes || 0,
      collateralRisk: 'low', // Default to low risk
      weightedScore: coa.score || 0.5,
      allocations: (coa.allocations || []).map(alloc => ({
        platformId: alloc.platformId,
        platformName: alloc.platformName,
        targetId: alloc.targetId ?? null,
        targetName: alloc.targetName ?? null,
        variantKey: alloc.variantKey ?? null,
        missileCount: alloc.missileCount,
        missiles: alloc.missiles || []
      })),
      recommended: coa.id === recommendedCoaId
    }));

    const requestBody = {
      mission_plan_id: missionPlanId,
      coas: transformedCOAs,
      recommended_coa_id: recommendedCoaId
    };

    // Include catalog_mission_id if provided
    if (catalogMissionId) {
      requestBody.catalog_mission_id = catalogMissionId;
    }

    return this.request(`/missions/${missionPlanId}/coas`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  /**
   * Get all COAs for a mission plan
   * @param {string} missionPlanId - Mission plan UUID
   * @returns {Promise<Object>} COAs with workflow stage info
   */
  async getCOAs(missionPlanId) {
    console.log(`📥 Retrieving COAs for mission plan: ${missionPlanId}`);
    return this.request(`/missions/${missionPlanId}/coas`);
  }

  /**
   * Archive or unarchive a mission plan
   * @param {string} missionPlanId - Mission plan UUID
   * @param {boolean} archived - True to archive, false to unarchive
   * @returns {Promise<Object>} Archive result
   */
  async archiveMissionPlan(missionPlanId, archived = true) {
    console.log(`${archived ? '📦 Archiving' : '📤 Unarchiving'} mission plan: ${missionPlanId}`);
    return this.request(`/missions/${missionPlanId}/archive?archived=${archived}`, {
      method: 'PATCH',
    });
  }

  /**
   * Soft-delete a Strike Plan (mission plan with COAs)
   * NOTE: This should only be used for Strike Plans, not Mission Plans
   * @param {string} missionPlanId - Mission plan UUID
   * @returns {Promise<Object>} Delete result
   */
  async deleteMissionPlan(missionPlanId) {
    console.log(`🗑️ Deleting Strike Plan: ${missionPlanId}`);
    return this.request(`/missions/${missionPlanId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Execution Planning ====================

  /**
   * Save execution plan data to a mission
   * @param {string} missionId - Mission identifier (e.g., 'operation_silent_viper')
   * @param {Object} executionData - Execution plan data
   * @param {Object} executionData.aiStrikePlan - AI-generated strike plan
   * @param {Array} executionData.platformAllocations - Platform assignments
   * @param {Object} executionData.timing - H-Hour and weather data
   * @param {Object} executionData.simulationResults - BDA simulation results
   * @param {Array} executionData.targets - Selected targets
   * @param {Array} executionData.airDefenses - Air defense systems
   * @param {string} executionData.status - Execution status (draft, ready, executing, complete)
   * @returns {Promise<Object>} Save result with updated timestamp
   */
  async saveExecutionPlan(missionId, executionData) {
    // The backend treats a missing status as 'ready' (published to Strike
    // Execution), so an implicit save could publish an unapproved draft.
    if (executionData?.status !== 'draft' && executionData?.status !== 'ready') {
      throw new Error(`saveExecutionPlan: status must be 'draft' or 'ready' (got ${executionData?.status})`);
    }
    console.log(`💾 Saving execution plan for mission: ${missionId}`);
    console.log(`   → Status: ${executionData.status}`);
    console.log(`   → strike plan: ${executionData.aiStrikePlan ? 'Yes' : 'No'}`);
    console.log(`   → Targets: ${executionData.targets?.length || 0}`);

    return this.request(`/catalog/missions/${missionId}/execution`, {
      method: 'PATCH',
      body: JSON.stringify(executionData),
    });
  }

  /**
   * Approve a rehearsed draft for execution: re-save the SAVED execution_data
   * with only its status changed. `expectedUpdatedAt` is the version that was
   * rehearsed; the server refuses (409) if the plan changed since.
   * @param {string} missionId - business mission id
   * @param {Object} executionData - the persisted execution_data, unmodified
   * @param {string} expectedUpdatedAt - server updated_at of the rehearsed version
   */
  async approveExecutionPlan(missionId, executionData, expectedUpdatedAt) {
    return this.request(`/catalog/missions/${missionId}/execution`, {
      method: 'PATCH',
      body: JSON.stringify({ ...executionData, status: 'ready', expected_updated_at: expectedUpdatedAt }),
    });
  }

  /**
   * Get execution plan data for a mission
   * @param {string} missionId - Mission identifier
   * @returns {Promise<Object>} Execution data object
   * @returns {Object} result.execution_data - Full execution plan (aiStrikePlan, platformAllocations, etc.)
   * @returns {string} result.execution_status - Current execution status
   * @returns {boolean} result.has_execution_data - Whether execution data exists
   */
  async getExecutionPlan(missionId) {
    return this.request(`/catalog/missions/${missionId}/execution`);
  }

  /**
   * Batch threat-avoidance route planning for a strike package (backend
   * RoutePlanner, A*). One threat picture, many platform→target pairs.
   * @param {Object} payload
   * @param {Array}  payload.pairs - [{ platform_id, target_id, start_lat, start_lon, target_lat, target_lon }]
   * @param {Array}  [payload.threat_zones] - [{ lat, lon, radius_km, type, effectiveness?, altitude_ceiling_ft? }]
   * @param {number} [payload.cruise_altitude_ft]
   * @returns {Promise<Object>} { planner, count, routes: [{ platform_id, target_id, waypoints, total_distance_km, ... }] }
   */
  async planRoutes(payload) {
    return this.request('/simulation/plan-routes', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ==================== AI Route Refinement ====================

  /**
   * Refine an already-selected strike route to satisfy analyst constraints.
   * @param {Object} payload
   * @param {Object}   payload.fpw       - First Planned Waypoint { name, lat, lng, altitude_ft }
   * @param {Object}   payload.target    - { name, lat, lng, type, hardening, terminal_bearing }
   * @param {Array}    payload.waypoints - ordered [{ name, lat, lng, altitude_ft }] (FPW first, target last)
   * @param {Array}    payload.threats   - [{ name, type, lat, lng, range_km }]
   * @param {Array}    payload.constraints - [{ kind, param?, text? }]
   * @param {string}  [payload.route_name]
   * @returns {Promise<Object>} { available, proposed_waypoints, summary, constraint_adherence, topography_risk, confidence, message }
   */
  async refineRoute(payload) {
    return this.request('/agents/route/constrain', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * The Strike Data Packages for a strike plan (EP-42): the released record for an
   * approved plan (source "persisted"), else a preview assembled from the saved plan.
   * @returns {Promise<Object>} { mission_id, execution_status, plan_version, source, preview, packages, warnings }
   */
  async getStrikePackages(missionId) {
    return this.request(`/catalog/missions/${encodeURIComponent(missionId)}/strike-packages`);
  }

  /**
   * Advisory: is a JDPI's terminal approach likely key to the effect, when it forces
   * the route into a turn-around? The numbers are the client's measurements.
   * @returns {Promise<Object>} { available, approach_criticality, rationale, suggested_direction, source, model, message }
   */
  async approachAdvice(payload) {
    return this.request('/agents/route/approach-advice', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ==================== After Action Review (AAR) ====================

  /**
   * Get AAR data for a mission
   * @param {string} missionId - Mission identifier
   * @returns {Promise<Object>} AAR response with data, status, and metadata
   */
  async getAAR(missionId) {
    return this.request(`/aar/missions/${missionId}/aar`);
  }

  /**
   * Save AAR data (draft or submit)
   * @param {string} missionId - Mission identifier
   * @param {Object} aarData - AAR data object
   * @param {string} status - AAR status ('draft' or 'submitted')
   * @param {string} submittedBy - Username of submitter
   * @param {string} analystRole - Analyst role (default: 'BDA_ANALYST')
   * @returns {Promise<Object>} Save result with status and timestamp
   */
  async saveAAR(missionId, aarData, status = 'draft', submittedBy = null, analystRole = 'BDA_ANALYST') {
    console.log(`💾 Saving AAR for mission: ${missionId}`);
    console.log(`   → Status: ${status}`);
    console.log(`   → Submitted by: ${submittedBy || 'Unknown'}`);

    return this.request(`/aar/missions/${missionId}/aar`, {
      method: 'PATCH',
      body: JSON.stringify({
        mission_id: missionId,
        status,
        aar_data: aarData,
        submitted_by: submittedBy,
        analyst_role: analystRole
      }),
    });
  }

  /**
   * Submit AAR for review (shortcut for saveAAR with status='submitted')
   * @param {string} missionId - Mission identifier
   * @param {Object} aarData - Complete AAR data object
   * @param {string} submittedBy - Username of submitter
   * @returns {Promise<Object>} Submit result
   */
  async submitAAR(missionId, aarData, submittedBy) {
    return this.saveAAR(missionId, aarData, 'submitted', submittedBy);
  }

  /**
   * Analyze AAR interview conversation using Claude AI
   * @param {Array} conversation - Array of {role, content} messages
   * @param {Object} context - Interview context (type, objectiveId, mission_objectives)
   * @returns {Promise<Object>} AI analysis with objectives_missed_ids, summary, recommendation, etc.
   */
  async analyzeAARInterview(conversation, context) {
    console.log(`   → Messages: ${conversation.length}`);
    console.log(`   → Context type: ${context.type}`);

    return this.request('/aar/aar/analyze-interview', {
      method: 'POST',
      body: JSON.stringify({
        conversation,
        context
      }),
    });
  }

  /**
   * Delete AAR data (admin only)
   * @param {string} missionId - Mission identifier
   * @returns {Promise<Object>} Delete confirmation
   */
  async deleteAAR(missionId) {
    return this.request(`/aar/missions/${missionId}/aar`, {
      method: 'DELETE',
    });
  }

  /**
   * List AARs with filters for dashboard
   * @param {Object} filters - Optional filters
   * @param {string} filters.status - AAR status filter
   * @param {string} filters.theater_id - Theater filter
   * @param {string} filters.start_date - Start date filter (ISO string)
   * @param {string} filters.end_date - End date filter (ISO string)
   * @returns {Promise<Object>} List of AAR items with total count
   */
  async listAARs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.theater_id) params.append('theater_id', filters.theater_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    const endpoint = queryString ? `/aar/list?${queryString}` : '/aar/list';

    return this.request(endpoint);
  }

  /**
   * Find similar missions for AI context
   * @param {Object} query - Query parameters
   * @param {string} query.theater_id - Theater to search in
   * @param {Array<string>} query.target_types - Target types to match
   * @param {number} query.min_confidence - Minimum confidence level (1-5)
   * @param {number} query.limit - Max results to return
   * @returns {Promise<Array>} List of similar missions with AAR data
   */
  async findSimilarMissions(query) {
    return this.request('/aar/similar-missions', {
      method: 'POST',
      body: JSON.stringify(query),
    });
  }

  /**
   * Get aggregated lessons learned
   * @param {Object} filters - Optional filters
   * @param {string} filters.theater_id - Theater filter (null = all)
   * @param {string} filters.category - Lesson category filter
   * @param {number} filters.min_confidence - Minimum confidence (default: 4)
   * @returns {Promise<Object>} Aggregated lessons with air defense performance data
   */
  async getAggregatedLessons(filters = {}) {
    const params = new URLSearchParams();
    if (filters.theater_id) params.append('theater_id', filters.theater_id);
    if (filters.category) params.append('category', filters.category);
    if (filters.min_confidence) params.append('min_confidence', filters.min_confidence);

    const queryString = params.toString();
    const endpoint = queryString ? `/aar/lessons?${queryString}` : '/aar/lessons';

    return this.request(endpoint);
  }

  // ==================== Health Check ====================

  /**
   * Check if API is reachable
   * @returns {Promise<boolean>} True if API is healthy
   */
  async healthCheck() {
    try {
      await fetch(`${this.baseURL.replace('/api', '')}/health`);
      return true;
    } catch (error) {
      console.warn('API health check failed:', error.message);
      return false;
    }
  }

  // ==================== Mission authoring AI (EP-44 usages) ====================
  // Each rejects with error.aiUnavailable (503) or error.aiOutputInvalid (502)
  // when no model-written suggestion is available; callers say so (EP-2).
  // `ctx` = {category, theater, combatant_command, focus_area, objective, target_names, target_types}.

  async suggestMissionTitles(ctx) {
    return this.request('/agents/mission/titles', { method: 'POST', body: JSON.stringify(ctx) });
  }

  async suggestMissionObjectives(ctx) {
    return this.request('/agents/mission/objectives', { method: 'POST', body: JSON.stringify(ctx) });
  }

  async generateStrategicContext(ctx) {
    return this.request('/agents/mission/strategic-context', { method: 'POST', body: JSON.stringify(ctx) });
  }

  async generateSuccessCriteria(ctx, answers) {
    return this.request('/agents/mission/success-criteria', {
      method: 'POST', body: JSON.stringify({ ...ctx, answers }),
    });
  }

  /** candidates: [{id, name, type, country}] -- the model chooses only from these. */
  async suggestTargetsForObjective(ctx, candidates, maxSuggestions = 8) {
    return this.request('/agents/mission/target-suggestions', {
      method: 'POST', body: JSON.stringify({ ...ctx, candidates, max_suggestions: maxSuggestions }),
    });
  }
}

// Export singleton instance
export default new APIClient();
